import { useState, useMemo } from "react";
import { SearchResultGroup, SearchResultItem } from "../types";
import { useDebouncedState } from "~/utils/useDebouncedInput";
import { getTopSearchResults, rankSearchResults } from "./rank-search-results";
import { groupSearchResults } from "./group-search-results";

// // // //

export function useFilteredSearchResults(props: {
    groups: SearchResultGroup[];
    initialQuery?: string;
}) {
    const [query, setQuery] = useState(props.initialQuery ?? "");
    const [value, onChange] = useDebouncedState<string>({
        debounceTimeout: 500,
        value: query,
        onChange: setQuery,
    });

    const [open, setOpen] = useState(false);

    // Memoize the flattening of all items from groups
    const items: SearchResultItem[] = useMemo(
        () =>
            props.groups.reduce((allItems, group) => {
                return [
                    ...allItems,
                    ...group.items.map((each) => {
                        return {
                            id: `${group.content.label}-${each.href}`,
                            content: each.content,
                            category: group.content.label,
                            href: each.href,
                            totalValue: each.totalValue,
                        };
                    }),
                ];
            }, [] as SearchResultItem[]),
        [props.groups],
    );

    // Memoize the filtering and sorting of items based on query
    const filteredItems = useMemo(
        () => rankSearchResults(items, query),
        [items, query],
    );
    const topResults = useMemo(
        () => getTopSearchResults(filteredItems, query),
        [filteredItems, query],
    );

    // Memoize the grouping of filtered items by category
    const searchGroupings = useMemo(
        () => groupSearchResults(filteredItems, query),
        [filteredItems, query],
    );

    return {
        query: value,
        setQuery: onChange,
        open,
        setOpen,
        searchGroupings,
        filteredItems,
        topResults,
    };
}
